/**
 * VoiceTranscriber — Pulsante mic con registrazione audio e trascrizione via ASR
 *
 * Stati: idle → recording → transcribing → idle
 * Usa MediaRecorder con audio/webm;codecs=opus
 * Dopo registrazione chiama /api/asr/transcribe
 * Inserisce il testo nel composer (NON invia automaticamente)
 */

import { useState, useRef, useCallback } from 'react'
import { Mic, Loader2, AlertCircle, Send } from 'lucide-react'
import api from '../services/api'

type VoiceState = 'idle' | 'recording' | 'transcribing' | 'error'

interface VoiceTranscriberProps {
  onTranscript: (text: string) => void
  disabled?: boolean
  className?: string
}

// Max recording duration (60 seconds)
const MAX_DURATION_MS = 60_000
// Max audio size (10 MB)
const MAX_SIZE_BYTES = 10 * 1024 * 1024

export default function VoiceTranscriber({ onTranscript, disabled, className = '' }: VoiceTranscriberProps) {
  const [state, setState] = useState<VoiceState>('idle')
  const [errorMsg, setErrorMsg] = useState<string>('')
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    mediaRecorderRef.current = null
    chunksRef.current = []
  }, [])

  const startRecording = useCallback(async () => {
    setErrorMsg('')

    // Check browser support
    if (!navigator.mediaDevices?.getUserMedia) {
      setState('error')
      setErrorMsg('Il browser non supporta la registrazione audio')
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      })
      streamRef.current = stream

      // Prefer opus codec
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4'

      const recorder = new MediaRecorder(stream, { mimeType })
      mediaRecorderRef.current = recorder
      chunksRef.current = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }

      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType })

        // Check size
        if (blob.size > MAX_SIZE_BYTES) {
          setState('error')
          setErrorMsg('Registrazione troppo lunga. Riprova con un messaggio più breve.')
          cleanup()
          return
        }

        if (blob.size < 100) {
          setState('error')
          setErrorMsg('Nessun audio registrato. Controlla il microfono.')
          cleanup()
          return
        }

        // Transcribe
        setState('transcribing')
        try {
          const formData = new FormData()
          formData.append('audio', blob, `recording.${mimeType.includes('webm') ? 'webm' : 'mp4'}`)

          const response = await api.post('/asr/transcribe', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 30000,
          })

          const text = response.data?.text?.trim()
          if (text) {
            onTranscript(text)
            setState('idle')
          } else {
            setState('error')
            setErrorMsg('Nessun testo riconosciuto. Riprova parlando più chiaramente.')
          }
        } catch (err) {
          console.error('[VoiceTranscriber] Transcription error:', err)
          setState('error')
          setErrorMsg('Errore nella trascrizione. Riprova.')
        }
        cleanup()
      }

      recorder.onerror = () => {
        setState('error')
        setErrorMsg('Errore durante la registrazione')
        cleanup()
      }

      recorder.start(250) // collect chunks every 250ms
      setState('recording')

      // Auto-stop after max duration
      timerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          mediaRecorderRef.current.stop()
        }
      }, MAX_DURATION_MS)

    } catch (err) {
      console.error('[VoiceTranscriber] getUserMedia error:', err)
      setState('error')
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setErrorMsg('Permesso microfono negato. Abilita il microfono nelle impostazioni del browser.')
      } else {
        setErrorMsg('Impossibile accedere al microfono')
      }
    }
  }, [cleanup, onTranscript])

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop()
    }
  }, [])

  const handleClick = useCallback(() => {
    if (state === 'recording') {
      stopRecording()
    } else if (state === 'idle' || state === 'error') {
      startRecording()
    }
    // During 'transcribing' — do nothing
  }, [state, startRecording, stopRecording])

  const isDisabled = disabled || state === 'transcribing'

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        disabled={isDisabled}
        title={
          state === 'recording' ? 'Invia registrazione'
            : state === 'transcribing' ? 'Trascrizione in corso...'
              : state === 'error' ? errorMsg
                : 'Registra messaggio vocale'
        }
        className={`
          px-3 py-3 rounded-xl border transition-all duration-200
          disabled:opacity-50 disabled:cursor-not-allowed
          ${state === 'recording'
            ? 'bg-red-500 border-red-500 text-white animate-pulse shadow-lg shadow-red-500/30'
            : state === 'transcribing'
              ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-600 text-blue-600 dark:text-blue-400'
              : state === 'error'
                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-600 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40'
                : 'text-gray-500 dark:text-gray-400 border-gray-300 dark:border-slate-600 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30'
          }
        `}
      >
        {state === 'recording' ? (
          <Send className="w-5 h-5" />
        ) : state === 'transcribing' ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : state === 'error' ? (
          <AlertCircle className="w-5 h-5" />
        ) : (
          <Mic className="w-5 h-5" />
        )}
      </button>

      {/* Recording indicator */}
      {state === 'recording' && (
        <span className="absolute -top-1 -right-1 flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
        </span>
      )}

      {/* Error tooltip */}
      {state === 'error' && errorMsg && (
        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-56 p-2 bg-amber-50 dark:bg-amber-900/80 border border-amber-200 dark:border-amber-700 rounded-lg text-xs text-amber-700 dark:text-amber-300 text-center shadow-lg z-50 pointer-events-none">
          {errorMsg}
        </div>
      )}
    </div>
  )
}
